import { controlFor } from '../components/edit-transaction-modal/edit-transaction-grid'
import {
  derivationRequest,
  FieldDerivation,
  FieldDerivationRequest,
} from '../edit-transaction/derivation'
import { TranCategoryResolution } from '../edit-transaction/tran-category'
import { BLOCK_HEADINGS } from '../edit-transaction/tran-category'
import {
  accountIdOf,
  collectionKeyOf,
  DerivationState,
  deriveFieldStatus,
  EditTransactionBreadcrumb,
  EditTransactionCollection,
  EditTransactionCollectionColumn,
  EditTransactionField,
  EditTransactionMode,
  isBlank,
  lineKey,
  listPendingOf,
  openingRawValue,
  projectEditTransaction,
  requiredUnsatisfied,
} from '../edit-transaction/edit-transaction.projection'
import { recordIdOf } from '../edit-transaction/transaction-category-wire'
import {
  editBaseline,
  SaveBaseline,
  SENT_READ_ONLY,
  toWireValue,
  writePath,
} from '../edit-transaction/transaction-save.assembly'
import {
  formatFieldValue,
  rawValueFrom,
} from '../edit-transaction/format-field-value'
import {
  TranCategoryKey,
  TransactionFieldMetadata,
} from '../models/transaction-metadata.model'
import { WorkbenchRow } from '../models/workbench-row.model'
import { DerivationResult } from '../services/transaction-derivation.service'
import { TransactionMetadataSource } from '../services/transaction-metadata.service'
import { TransactionValueSource } from '../services/transaction-value.service'

/** The Bulk Transactions grid — the same documents the single dialog shows, transposed. */

/** Create is not a bulk flow; the other two modes are. */
export type BulkTransactionMode = Extract<EditTransactionMode, 'edit' | 'post'>

/** One selected transaction, with the tran category the host resolved for it. */
export interface BulkTransactionEntry {
  row: WorkbenchRow
  resolution: TranCategoryResolution
}

export interface BulkTransactionInput {
  mode: BulkTransactionMode
  entries: readonly BulkTransactionEntry[]
  metadata: TransactionMetadataSource
  values: TransactionValueSource
  /** What the opening derivations have answered so far (PVAL-26685); absent, nothing has. */
  derived?: BulkDerived
}

/**
 * What the dialog's `context` derivations have answered, by account (PVAL-26685).
 *
 * A bulk selection can span accounts, and a context derivation is one account's own answer, so each
 * row reads the answers of ITS account. `values` holds what came back; `states` holds the
 * derivations still out (`pending`) or refused (`failed`), which stop holding Save: a read-only field
 * nobody can fill in is not something a transaction can be short of. A field in neither is left as
 * its row opened with it.
 */
export interface BulkDerived {
  values: ReadonlyMap<string, ReadonlyMap<string, unknown>>
  states: ReadonlyMap<string, ReadonlyMap<string, DerivationState>>
}

/**
 * The host's running record of the opening derivations, handed to every projection as
 * {@link BulkDerived}. The host owns it, so a re-projection after a catalog or trade fill keeps what
 * the derivations already answered.
 */
export class BulkDerivations implements BulkDerived {
  readonly values = new Map<string, Map<string, unknown>>()
  readonly states = new Map<string, Map<string, DerivationState>>()

  /** One account's derivations have been asked and not yet answered. */
  begin(accountId: string, requests: readonly FieldDerivationRequest[]): void {
    this.states.set(
      accountId,
      new Map<string, DerivationState>(
        requests.map(request => [request.field, 'pending']),
      ),
    )
  }

  /**
   * Record what one account's call answered, field by field, the way the single dialog does: an error
   * or no result at all marks the field `failed` and leaves it as it was, `null` means the upstream
   * had nothing to say, and a value is written.
   */
  settle(
    accountId: string,
    requests: readonly FieldDerivationRequest[],
    results: readonly DerivationResult[],
  ): void {
    const byKey = new Map(results.map(result => [result.keyName, result]))
    const values = this.values.get(accountId) ?? new Map<string, unknown>()
    const states =
      this.states.get(accountId) ?? new Map<string, DerivationState>()
    for (const { field, keyName } of requests) {
      const result = byKey.get(keyName)
      if (!result || result.error != null) {
        console.error(
          `[transactions-2] could not derive ${field} for account ${accountId}; its rows keep their value`,
          result?.error ?? 'no result',
        )
        states.set(field, 'failed')
        continue
      }
      states.delete(field)
      if (result.value != null) values.set(field, result.value)
    }
    this.values.set(accountId, values)
    this.states.set(accountId, states)
  }
}

/**
 * The `/resolve` items one account's `context` derivations need.
 *
 * Only `context` ones: a bulk grid has no cell to trigger a field derivation from, and a per-line one
 * belongs to the items modal. A derivation whose input is unset asks nothing.
 */
export function bulkOpeningDerivations(
  derivations: readonly FieldDerivation[],
  accountId: string,
): FieldDerivationRequest[] {
  const context: Readonly<Record<string, unknown>> = { accountId }
  return derivations.flatMap(derivation => {
    if (derivation.from !== 'context') return []
    const request = derivationRequest(
      derivation,
      () => undefined,
      key => context[key],
    )
    return request ? [request] : []
  })
}

/** Which control a bulk cell renders. */
export type BulkCellKind =
  | 'readOnly'
  | 'collection'
  | 'enum'
  | 'lookup'
  | 'date'
  | 'number'
  | 'text'

/** One column of the grid — a field of the shared tran category. */
export interface BulkTransactionColumn {
  field: string
  label: string
  /** The display group the field sits in. */
  group: string
  required: boolean
  kind: BulkCellKind
  /** Rendered width in px. See `bulk-transaction-grid.ts` for the rule. */
  width: number
  /** Set when this is an Item column rather than a field of the transaction itself. */
  item?: BulkItemColumn
}

/**
 * An Item N column: one column of a collection's line, shown inline in the grid.
 *
 * The lines are edited in place rather than behind the items modal, so a payment can be filled
 * across twenty transactions without opening twenty dialogs. `line` is the line the column reads,
 * which is what lets two transactions holding different numbers of lines share one set of columns:
 * Item 1 is line 0 of each row's own collection, and a row that has no line 0 yet simply reads
 * blank until something is typed into it.
 */
export interface BulkItemColumn {
  collection: string
  /** 0-based line this column reads and writes. */
  line: number
  /** The leaf key inside the line — the collection column's own field. */
  leaf: string
  /** Its position in the collection's `columns`, which is the order a line's values are stored in. */
  slot: number
}

/** The grid key an item cell is addressed by: unique per collection, line and column. */
export const itemFieldKey = (
  collection: string,
  line: number,
  leaf: string,
): string => `${collection}#${line}.${leaf}`

/** One transaction — one row of the grid. */
export interface BulkTransactionRow {
  rowId: string
  /** What this row IS, for the gutter's tooltip. */
  title: string
  /**
   * The raw account this transaction belongs to, which its lookups search under.
   *
   * Per row rather than per grid: a selection can span several accounts, and a search asked under the
   * wrong one is answered for another account's client.
   */
  accountId: string
  fields: Readonly<Record<string, EditTransactionField>>
  /** The row's collections, so a summary cell can open its lines. */
  collections: EditTransactionCollection[]
  /** The formatted value every field opened with, by name. */
  original: Readonly<Record<string, string>>
  /**
   * The lines every collection opened with, by collection name.
   *
   * Copied rather than referenced: an Item cell writes into `collections`, and a record of what a
   * line held is worth nothing if the write reaches it too.
   */
  originalLines: Readonly<Record<string, readonly string[][]>>
  /**
   * The id an EDIT saves this row under (`recordIdOf`: `gLEntryId`, else `tradeId`), or null on
   * post, which creates (PVAL-26440).
   */
  recordId: string | null
  /**
   * The typed values the row opened with — what the save diffs an edit against. Taken from the
   * projection before the account's derived answers are written in, exactly as the single dialog
   * takes its baseline, so a derived value is sent as the change it is. Null on post.
   */
  baseline: SaveBaseline | null
}

/** Why the grid refuses to open, and what the selection actually contained. */
export interface BulkTransactionBlock {
  heading: string
  note: string
  /** One entry per distinct type in the selection. Empty when there is nothing to choose between. */
  types: { label: string; count: number }[]
}

export interface BulkTransactionViewModel {
  mode: BulkTransactionMode
  title: string
  breadcrumb: EditTransactionBreadcrumb | null
  blocked: boolean
  block: BulkTransactionBlock | null
  /** Every column the shared tran category declares, required and optional alike. */
  columns: BulkTransactionColumn[]
  /** The footer fields, with their values deliberately EMPTY. */
  footer: EditTransactionField[]

  /** How many transactions the grid holds. */
  rowCount: number

  /**
   * The most lines any selected transaction holds, per collection name.
   *
   * What decides how many Item columns a collection opens with: a selection where one transaction
   * carries two payments and another carries one opens both, so neither is hiding a value behind a
   * column nobody asked for.
   */
  openingItems: Readonly<Record<string, number>>

  /**
   * The `context` derivations of the shared tran category (PVAL-26685), which the host asks for once
   * per account when the grid opens. Field-triggered and per-line derivations are not run here.
   */
  derivations: FieldDerivation[]

  /**
   * One transaction, projected on first ask and cached from then on.
   *
   * Lazy because projecting eagerly does not survive the selection sizes this grid has to take: a
   * full `projectEditTransaction` costs ~0.5ms and ~34KB, so 30,000 rows measured at 13.1 seconds
   * and 1,008MB before the dialog painted. Every row still projects through exactly the same
   * function — a column here is the same field the single dialog renders as a row — it just does so
   * when something asks for it.
   *
   * The cache is unbounded on purpose: a row the user has edited holds that edit in its projected
   * field objects, so evicting it would silently discard typing. Bounding it needs the edits to
   * live somewhere other than the projection, which is a later phase.
   */
  rowAt(index: number): BulkTransactionRow

  /**
   * The row at an index only if it has already been projected, else null.
   *
   * Lets a caller ask "which of the rows I am already holding owns this field?" without the question
   * itself projecting the other 29,999.
   */
  projectedAt(index: number): BulkTransactionRow | null

  /**
   * Required values each row is short of, as the grid OPENS — by row index.
   *
   * Scanned without projecting anything (see {@link scanOpeningMissing}), because the summary bar
   * reports a number over every transaction and that number cannot be the thing that forces 30,000
   * projections. From the first edit onward the component maintains this incrementally.
   */
  openingMissing: number[]

  /** The tran category every row shares (PVAL-26440) — what the save's `category` is decided from. */
  tranCategory: TranCategoryKey | null

  /**
   * The raw account of the row at an index, read off the row rather than its projection — what the
   * save groups requests by, for 30,000 rows without projecting one (PVAL-26440).
   */
  accountAt(index: number): string

  /**
   * The record a POST of the row at an index would send, assembled WITHOUT projecting it
   * (PVAL-26440): every editable field's opening value (`openingRawValue`) with the account's
   * derived answers written in, typed and nested exactly as `assembleCreateRecord` does for a
   * projected row. A post sends every row, and projecting them all to send them is the cost this
   * grid exists to avoid. Footer values are not included — they are the session's, added by the
   * caller. Only meaningful on post.
   *
   * @throws SaveAssemblyError when a value will not type
   */
  rawRecordAt(index: number): Record<string, unknown>
}

/** Starting width per control kind, from the prototype's own `BULK_W`. */
const KIND_WIDTH: Readonly<Record<BulkCellKind, number>> = {
  readOnly: 126,
  collection: 108,
  enum: 146,
  lookup: 146,
  date: 108,
  number: 118,
  text: 148,
}

const LABEL_CHAR_WIDTH = 6.2
const LABEL_PADDING = 22
const REQUIRED_MARK_WIDTH = 12
/** The expand glyph a collection's header carries, and the room that keeps it off the name. */
const HEADER_GLYPH_WIDTH = 24

/** Past this a column is taking room from every column beside it, so it truncates instead. */
const MAX_COLUMN_WIDTH = 340

export function bulkColumnWidth(
  label: string,
  required: boolean,
  kind: BulkCellKind,
): number {
  const header =
    Math.ceil(label.length * LABEL_CHAR_WIDTH) +
    LABEL_PADDING +
    (required ? REQUIRED_MARK_WIDTH : 0) +
    // A collection's header carries a glyph at its far edge. Without room for it the name was
    // truncated — `Other Paymen…` — because the glyph is a control and does not give way.
    (kind === 'collection' ? HEADER_GLYPH_WIDTH : 0)
  return Math.min(MAX_COLUMN_WIDTH, Math.max(KIND_WIDTH[kind], header))
}

/**
 * Heading and note for a selection spanning more than one type.
 *
 * The note is the same whatever the counts are, because the type list under it already says how many
 * of each there are.
 */
const MIXED_HEADING = 'All transactions must be of the same type'

const MIXED_NOTE =
  'Bulk Edit can only be used for a single transaction type at a time. ' +
  'Please refine your selection and try again.'

/** The bucket a resolution falls in. */
const bucketOf = (resolution: TranCategoryResolution): string =>
  resolution.kind === 'tranCategory'
    ? resolution.tranCategory
    : `blocked:${resolution.reason}`

const bucketLabel = (
  resolution: TranCategoryResolution,
  metadata: TransactionMetadataSource,
): string =>
  resolution.kind === 'tranCategory'
    ? metadata.tranCategory(resolution.tranCategory)?.name ??
      resolution.tranCategory
    : BLOCK_HEADINGS[resolution.reason]

/** The account the header chip names. */
const accountChip = (accounts: (string | null)[]): string | null => {
  const named = accounts.filter((value): value is string => !!value)
  if (!named.length) return null
  const distinct = [...new Set(named)]
  return distinct.length === 1 ? distinct[0] : `${distinct.length} accounts`
}

const blockedModel = (
  mode: BulkTransactionMode,
  block: BulkTransactionBlock,
): BulkTransactionViewModel => ({
  mode,
  title: mode === 'post' ? 'Post to Enfusion' : 'Edit Transactions',
  breadcrumb: null,
  blocked: true,
  block,
  columns: [],
  footer: [],
  rowCount: 0,
  rowAt: () => {
    throw new Error('A blocked bulk grid has no rows to project.')
  },
  projectedAt: () => null,
  openingMissing: [],
  openingItems: {},
  derivations: [],
  tranCategory: null,
  accountAt: () => '',
  rawRecordAt: () => {
    throw new Error('A blocked bulk grid has no rows to save.')
  },
})

/** A footer field as the SESSION holds it: the metadata's own definition, opened empty. */
const sessionFooterField = (
  field: EditTransactionField,
): EditTransactionField => ({
  ...field,
  value: '',
  rawValue: null,
  status: 'novalue',
})

/**
 * A projected field with what its account's derivation answered written into it: the single
 * dialog's `write` and `settle`, applied before the row records what it opened with, so a derived
 * value never reads as an edit.
 */
const withDerived = (
  field: EditTransactionField,
  answers: ReadonlyMap<string, unknown> | undefined,
  states: ReadonlyMap<string, DerivationState> | undefined,
): EditTransactionField => {
  const answer = answers?.get(field.field)
  const state = states?.get(field.field)
  if (answer == null && state == null) return field
  const rawValue =
    answer == null ? field.rawValue : rawValueFrom(answer, field.style)
  const filled: EditTransactionField = {
    ...field,
    rawValue,
    value:
      answer == null ? field.value : formatFieldValue(rawValue, field.style),
    derivationState: state ?? field.derivationState,
  }
  return { ...filled, status: deriveFieldStatus(filled) }
}

/** Project a selection into the bulk grid. */
export function projectBulkTransaction(
  input: BulkTransactionInput,
): BulkTransactionViewModel {
  const { mode, entries, metadata, values, derived } = input

  if (!entries.length) {
    return blockedModel(mode, {
      heading: 'Nothing to edit',
      note: 'No transactions were selected.',
      types: [],
    })
  }

  // Grouped in encounter order, so the refusal lists the types the way the selection ran.
  const buckets = new Map<string, { label: string; count: number }>()
  for (const entry of entries) {
    const key = bucketOf(entry.resolution)
    const seen = buckets.get(key)
    if (seen) seen.count++
    else
      buckets.set(key, {
        label: bucketLabel(entry.resolution, metadata),
        count: 1,
      })
  }

  if (buckets.size > 1) {
    return blockedModel(mode, {
      heading: MIXED_HEADING,
      note: MIXED_NOTE,
      types: [...buckets.values()],
    })
  }

  const first = entries[0]
  if (first.resolution.kind === 'blocked') {
    const only = [...buckets.values()][0]
    return blockedModel(mode, {
      heading: BLOCK_HEADINGS[first.resolution.reason],
      note:
        `None of the ${entries.length} selected transactions can be opened: ` +
        `all ${entries.length} are ${only.label.toLowerCase()}.`,
      types: [],
    })
  }

  const tranCategory: TranCategoryKey = first.resolution.tranCategory

  const projectRow = (index: number) =>
    projectEditTransaction({
      mode,
      resolution: entries[index].resolution,
      row: entries[index].row,
      counterparts: [],
      metadata,
      values,
    })

  // The SCHEMA comes from one projection and nothing else. Every row here resolved to the same tran
  // category, and the field list is a pure function of that category, so the columns and the footer
  // are identical across rows by construction — projecting a second row to learn them again is the
  // cost this grid cannot afford.
  const schema = projectRow(0)

  const columns: BulkTransactionColumn[] = schema.sections.flatMap(section =>
    section.fields.map(field => {
      const kind = bulkCellKind(field, schema.collections)
      return {
        field: field.field,
        label: field.label,
        group: section.name,
        required: field.required,
        kind,
        width: bulkColumnWidth(field.label, field.required, kind),
      }
    }),
  )

  const toRow = (
    index: number,
    vm: ReturnType<typeof projectRow>,
  ): BulkTransactionRow => {
    // A row reads the derivations of ITS account: a selection can span several.
    const account = accountIdOf(entries[index].row)
    const answers = derived?.values.get(account)
    const states = derived?.states.get(account)
    const fields: Record<string, EditTransactionField> = {}
    const original: Record<string, string> = {}
    for (const section of vm.sections) {
      for (const projected of section.fields) {
        const field = withDerived(projected, answers, states)
        fields[field.field] = field
        original[field.field] = field.value
      }
    }
    const originalLines: Record<string, string[][]> = {}
    for (const collection of vm.collections) {
      originalLines[collection.name] = collection.rows.map(line => [...line])
    }
    return {
      rowId: entries[index].row.rowId,
      title: rowTitle(entries[index].row),
      accountId: account,
      fields,
      collections: vm.collections,
      original,
      originalLines,
      recordId: mode === 'edit' ? recordIdOf(entries[index].row.fields) : null,
      // `vm` is the projection BEFORE `withDerived` — see the field's own note.
      baseline: mode === 'edit' ? editBaseline(vm) : null,
    }
  }

  const cache = new Map<number, BulkTransactionRow>()
  // Row 0 is already projected — it was the schema — so it is seeded rather than projected twice.
  cache.set(0, toRow(0, schema))

  const rowAt = (index: number): BulkTransactionRow => {
    const hit = cache.get(index)
    if (hit) return hit
    const built = toRow(index, projectRow(index))
    cache.set(index, built)
    return built
  }

  // PVAL-26440: the post record of a row nobody has projected. The field list is the schema's — a
  // pure function of the shared tran category — so it is read once, not per row.
  const schemaFields = metadata.fieldsFor(tranCategory)
  const rawRecordAt = (index: number): Record<string, unknown> => {
    const row = entries[index].row
    const account = accountIdOf(row)
    const answers = derived?.values.get(account)
    const states = derived?.states.get(account)
    const record: Record<string, unknown> = {}
    const shape = (meta: TransactionFieldMetadata) => ({
      field: meta.field,
      label: meta.label,
      dataType: meta.dataType,
    })

    for (const meta of schemaFields) {
      // The footer is the session's, added by the caller; a column is sent through its collection.
      if (meta.present === 'footerField') continue
      if (meta.displayContainer === 'arrayField') continue

      if (meta.displayContainer === 'arraySummary') {
        if (!meta.settings.edit) continue
        const key = collectionKeyOf(meta)
        const lineColumns = schemaFields.filter(
          column =>
            column.displayContainer === 'arrayField' &&
            column.collection === key,
        )
        if (meta.dataType === 'object') {
          // One nested object (an FX Swap leg): its children by path, as a top-level dotted field.
          for (const column of lineColumns) {
            if (!column.settings.edit) continue
            const wire = toWireValue(
              shape(column),
              openingRawValue(column, row, mode),
            )
            if (wire != null) writePath(record, column.field, wire)
          }
          continue
        }
        const raw = row.fields[key]
        const lines = (Array.isArray(raw) ? raw : [])
          .filter(
            (line): line is Record<string, unknown> =>
              typeof line === 'object' && line !== null,
          )
          .map(line => {
            const sent: Record<string, unknown> = {}
            for (const column of lineColumns) {
              const leaf = lineKey(column.field)
              const wire = toWireValue(shape(column), line[leaf])
              if (wire != null) sent[leaf] = wire
            }
            return sent
          })
        if (lines.length) record[key] = lines
        continue
      }

      if (!meta.settings.edit && !SENT_READ_ONLY.has(meta.field)) continue
      const state = states?.get(meta.field)
      if (!meta.settings.edit && state != null && state !== 'idle') continue
      const answer = answers?.get(meta.field)
      const value =
        answer == null
          ? openingRawValue(meta, row, mode)
          : rawValueFrom(answer, meta.displayStyle)
      const wire = toWireValue(shape(meta), value)
      if (wire != null) writePath(record, meta.field, wire)
    }
    return record
  }

  return {
    mode,
    title: mode === 'post' ? 'Post to Enfusion' : 'Edit Transactions',
    breadcrumb: {
      source: schema.breadcrumb?.source ?? '',
      tranCategory:
        metadata.tranCategory(tranCategory)?.name ?? String(tranCategory),
      // Read off the rows, not off their projections: the chip must say something true of all of
      // them, and projecting 30,000 records to learn their account ids is the whole problem.
      account: accountChip(entries.map(entry => entry.row.accountId ?? null)),
    },
    blocked: false,
    block: null,
    columns,
    footer: schema.footer.map(sessionFooterField),
    rowCount: entries.length,
    rowAt,
    projectedAt: index => cache.get(index) ?? null,
    openingMissing: scanOpeningMissing(
      entries,
      mode,
      metadata,
      tranCategory,
      derived,
    ),
    openingItems: scanOpeningItems(entries, schema.collections),
    derivations: schema.derivations.filter(
      derivation => derivation.from === 'context',
    ),
    tranCategory,
    accountAt: index => accountIdOf(entries[index]?.row ?? null),
    rawRecordAt,
  }
}

/**
 * The most lines any selected transaction holds, per collection.
 *
 * Read off the raw rows rather than their projections, for the same reason {@link
 * scanOpeningMissing} is: the answer decides what the grid opens showing, and projecting 30,000
 * records to learn it is the cost this grid exists to avoid. A collection no row has a line for
 * answers 0, which is what leaves it collapsed.
 */
export function scanOpeningItems(
  entries: readonly BulkTransactionEntry[],
  collections: readonly EditTransactionCollection[],
): Record<string, number> {
  const most: Record<string, number> = {}
  for (const collection of collections) {
    let lines = 0
    for (const entry of entries) {
      const raw = entry.row.fields[collection.name]
      if (Array.isArray(raw)) lines = Math.max(lines, raw.length)
    }
    most[collection.name] = lines
  }
  return most
}

/** What the gutter's tooltip says a row is: its security, and the id it will be saved under. */
const rowTitle = (row: WorkbenchRow): string =>
  [row.sec, row.rowId].filter(Boolean).join(' · ')

/** Which control a bulk cell renders. */
export function bulkCellKind(
  field: EditTransactionField,
  collections: readonly EditTransactionCollection[],
): BulkCellKind {
  if (collections.some(entry => entry.name === field.field)) return 'collection'
  return bulkControlKind(field)
}

/**
 * The control a value renders in, from the properties that decide it.
 *
 * A top-level field and a column of a collection answer this the same way and carry the same
 * properties to answer it with, so the rule is stated once. Only the collection test above is the
 * field's alone: a line column cannot itself be a collection.
 */
export function bulkControlKind(
  shape: Pick<
    EditTransactionCollectionColumn,
    'editable' | 'listValues' | 'listType' | 'dataType'
  >,
): BulkCellKind {
  // The one state that REPLACES a control rather than choosing one, exactly as the single dialog's
  // template does it: locked text in place of a control when the metadata says the field cannot be
  // edited.
  //
  // `listPending` is deliberately not consulted anywhere in this grid. A list reading
  // `status: unavailable` has not had its OPTIONS answered for yet; it is not a statement that the
  // field cannot be edited, and `settings.edit` already is. Reading it turned every `edit: true`
  // enum carrying that status — Trade Currency, Settle Currency, Financing ID — into a cell that
  // showed a loading glyph and opened nothing. The cell renders its picker and the picker opens,
  // empty until the resolve endpoint answers and populated the moment it does.
  if (!shape.editable) return 'readOnly'
  // Which control, asked of the SAME function the single dialog and the items modal ask. Stated
  // twice, the two drifted: this one wanted options to exist before it would call a field an enum,
  // so a closed set whose values had not arrived degraded to a free text box here while the dialog
  // beside it opened an empty picker. `controlFor` is the one answer to that question.
  return controlFor(shape)
}

/** One Item band: every column of a collection's line, for the line at `line`. */
export function bulkItemColumns(
  collection: EditTransactionCollection,
  line: number,
): BulkTransactionColumn[] {
  return collection.columns.map((column, slot) => {
    const kind = bulkControlKind(column)
    return {
      field: itemFieldKey(collection.name, line, column.field),
      label: column.label,
      group: `Item ${line + 1}`,
      required: column.required,
      kind,
      width: bulkColumnWidth(column.label, column.required, kind),
      item: { collection: collection.name, line, leaf: column.field, slot },
    }
  })
}

/**
 * How many required values each row is short of, as the grid opens — projecting nothing.
 *
 * The summary bar reports "N required missing" over the whole selection and the action bar gates
 * Save on it, so this number is needed for every transaction the moment the grid opens. Reaching it
 * through {@link projectEditTransaction} is what cost 13.1 seconds and 1,008MB at 30,000 rows; the
 * same answer comes from reading each required field's raw value and asking the same predicate.
 *
 * It must not drift from the projected verdict, so everything it decides, it decides through shared
 * code: {@link openingRawValue} for what a field opens holding, {@link listPendingOf} for the
 * disabled-list exemption, and {@link requiredUnsatisfied} for the verdict itself. The only thing
 * reconstructed here is `status`, and only because `novalue` is by definition `isBlank(rawValue)`.
 */
export function scanOpeningMissing(
  entries: readonly BulkTransactionEntry[],
  mode: BulkTransactionMode,
  metadata: TransactionMetadataSource,
  tranCategory: TranCategoryKey,
  derived?: BulkDerived,
): number[] {
  const all = metadata.fieldsFor(tranCategory)

  /**
   * The population the verdict counts, and nothing else.
   *
   * `fieldsFor` returns every shown field of the category, but the projection splits them three
   * ways and only ONE of those lands in the rows a verdict walks:
   *
   *  - `footerField` goes to the footer, which is one session-level input counted once by
   *    `bulkFooterMissing` — counting it per row too made every row report the empty Notes box;
   *  - `arrayField` is a collection COLUMN, counted below over the lines rather than here (and
   *    `openingRawValue` cannot read one anyway: the dotted path walks into an array and comes back
   *    undefined, so every one of them read as missing);
   *  - `default` and `arraySummary` are the rows.
   */
  const rowFields = all.filter(
    meta =>
      meta.present !== 'footerField' &&
      (meta.displayContainer === 'default' ||
        meta.displayContainer === 'arraySummary') &&
      meta.settings.required &&
      !listPendingOf(meta),
  )

  // A collection's own required LINE columns, grouped by the key its lines live under. The items
  // modal marks a blank one, so a grid that ignored them would offer Save against a state it is
  // itself rendering as unfinished.
  const lineColumns = new Map<string, TransactionFieldMetadata[]>()
  for (const meta of all) {
    if (
      meta.displayContainer !== 'arrayField' ||
      !meta.collection ||
      !meta.settings.required ||
      listPendingOf(meta)
    ) {
      continue
    }
    const held = lineColumns.get(meta.collection) ?? []
    held.push(meta)
    lineColumns.set(meta.collection, held)
  }

  const linesAt = (
    entry: BulkTransactionEntry,
    collection: string,
  ): unknown[] => {
    const raw = entry.row.fields[collection]
    return Array.isArray(raw) ? raw : []
  }

  return entries.map(entry => {
    let missing = 0
    // The derivations of THIS row's account, once any have been asked.
    const account = derived ? accountIdOf(entry.row) : ''
    const answers = derived?.values.get(account)
    const states = derived?.states.get(account)

    for (const meta of rowFields) {
      // A summary holds its collection's AGGREGATE, not the field's own value — and the aggregate
      // is blank exactly when there are no lines to aggregate (`summaryValue` returns undefined on
      // an empty collection, and a number or a line value on any non-empty one).
      if (meta.displayContainer === 'arraySummary') {
        if (!linesAt(entry, meta.collection ?? meta.field).length) missing++
        continue
      }

      // A derived answer is what the projection will show, so it is what gets judged.
      const answer = answers?.get(meta.field)
      const rawValue =
        answer == null ? openingRawValue(meta, entry.row, mode) : answer
      const unsatisfied = requiredUnsatisfied({
        required: true,
        requiredParameter: meta.settings.requiredParameter ?? null,
        rawValue,
        // `novalue` is exactly `isBlank(rawValue)` — see `deriveFieldStatus`. Any other status
        // leaves the verdict to the parameterised rule, which reads `rawValue` itself.
        status: isBlank(rawValue) ? 'novalue' : 'match',
        listPending: false,
        editable: meta.settings.edit,
        derivationState: states?.get(meta.field) ?? 'idle',
      })
      if (unsatisfied) missing++
    }

    for (const [collection, columns] of lineColumns) {
      for (const line of linesAt(entry, collection)) {
        if (typeof line !== 'object' || line === null) continue
        const held = line as Record<string, unknown>
        for (const column of columns) {
          // A line is keyed by the leaf alone: it already sits inside the collection.
          const leaf = column.field.split('.').pop() ?? column.field
          if (isBlank(held[leaf])) missing++
        }
      }
    }

    return missing
  })
}
